import { TableRow } from "@mui/material";
import { sum } from "lodash";
import { useContext, useEffect, useState } from "react";
import { Material } from "../../../../data/entities";
import { AuthenticationContext } from "../../../../provider/AuthenticationProvider";
import CustomTable from "../../../../shared/components/CustomTable";
import { StyledTableCell } from "../../../../shared/components/CustomTable/Styled";
import IconButton from "../../../../shared/components/IconButton";
import TextBox from "../../../../shared/components/TextBox";
import { StyledNavLink } from "../../../../shared/styled/TableStyles";
import { color } from "../../../../shared/styles";
import DeleteIcon from "../../../../shared/svg/DeleteIcon";
import WarningTableIcon from "../../../../shared/svg/WarningTableIcon";
import { formatNumber } from "../../../../utils/format";
import { TableWarningMessage } from "../../../ProductOrder/single/AddProductModal/Styled";
import { ContentWrapper, ProductButtonRow, ProductTableText } from "../../../ProductOrder/single/Styled";
import {
    SummaryTableCell,
    SummaryTableRow,
} from "../../../Provider/create/Content/MaterialList/AddMaterialModal/Styled";
import { StyledButton } from "../../Styled";
import { AddMaterialModal } from "../AddMaterialModal";

const STICKY_COL_1_WIDTH = "96px";
const STICKY_COL_2_LEFT = "96px";
const MATERIAL_NAME_WIDTH = "200px";
const UNIT_COL_WIDTH = "64px";
const QUANTITY_COL_WIDTH = "100px";
const ACTION_COL_WIDTH = "44px";
const ROW_CELL_SX = {
    py: "8px",
    lineHeight: "20px",
    verticalAlign: "middle",
};
const DECIMAL_QUANTITY_REGEX = /^\d*(\.\d*)?$/;
const QUANTITY_ROUNDING_FACTOR = 1e10;

const compactCellSx = {
    px: "6px",
    py: "8px",
    lineHeight: "18px",
    textAlign: "center",
    maxWidth: UNIT_COL_WIDTH,
    "& div": {
        justifyContent: "center",
        gap: "4px",
    },
};

const compactQuantityCellSx = {
    px: "8px",
    py: "8px",
    lineHeight: "18px",
    textAlign: "right",
    maxWidth: QUANTITY_COL_WIDTH,
    "& div": {
        justifyContent: "flex-end",
        gap: "4px",
    },
};

const roundQuantity = (value: number) => (
    Math.round((value + Number.EPSILON) * QUANTITY_ROUNDING_FACTOR) / QUANTITY_ROUNDING_FACTOR
);

const formatEditableQuantity = (value: number | string) => {
    const n = typeof value === "number" ? value : Number.parseFloat(value);
    if (!Number.isFinite(n)) return "";

    return new Intl.NumberFormat("en-US", {
        useGrouping: false,
        maximumFractionDigits: 10,
    }).format(roundQuantity(n));
};

export const MaterialTableColumns = [
    {
        text: "Mã vật tư",
        icon: <></>,
        width: STICKY_COL_1_WIDTH,
        sx: { position: "sticky", left: 0, zIndex: 3, backgroundColor: "#F2F4FA" },
    },
    {
        text: "Tên vật tư",
        icon: <></>,
        width: MATERIAL_NAME_WIDTH,
        sx: { position: "sticky", left: STICKY_COL_2_LEFT, zIndex: 3, backgroundColor: "#F2F4FA" },
    },
    { text: "Đơn vị chính", icon: <></>, width: UNIT_COL_WIDTH, sx: compactCellSx },
    { text: "Số lượng yêu cầu (ĐVC)", icon: <></>, width: QUANTITY_COL_WIDTH, sx: compactQuantityCellSx },
    { text: "Đơn vị phụ", icon: <></>, width: UNIT_COL_WIDTH, sx: compactCellSx },
    { text: "Số lượng yêu cầu (ĐVP)", icon: <></>, width: QUANTITY_COL_WIDTH, sx: compactQuantityCellSx },
    { text: "Số lượng xuất", icon: <></>, width: QUANTITY_COL_WIDTH, sx: compactQuantityCellSx },
    { text: "", icon: <></>, width: ACTION_COL_WIDTH, sx: { px: "4px", maxWidth: ACTION_COL_WIDTH } },
];

interface ProductRowProps {
    item: any;
    extraRowProps: any;
}

export const MaterialRow = ({ item, extraRowProps }: ProductRowProps) => {
    const quantity = item.missing_quantity !== undefined ? item.missing_quantity : 0;
    const quantitySecondaryMeasure = quantity / (item.conversion_rate || 1);
    const [isEditingQuantity, setIsEditingQuantity] = useState(false);
    const [quantityText, setQuantityText] = useState(() => formatEditableQuantity(quantitySecondaryMeasure));
    const originalQuantity =
        extraRowProps.originalQuantities?.[item.id] !== undefined
            ? extraRowProps.originalQuantities[item.id]
            : 0;

    const isQuantityMatched = quantity >= originalQuantity;
    const backgroundColor = isQuantityMatched ? "" : color.status.DANGER_LIGHT;
    const selectedQuantityBorderColor = isQuantityMatched ? "" : color.status.FAIL;

    useEffect(() => {
        if (!isEditingQuantity) {
            setQuantityText(formatEditableQuantity(quantitySecondaryMeasure));
        }
    }, [isEditingQuantity, quantitySecondaryMeasure]);

    const onChangeQuantityText = (value: string) => {
        if (!DECIMAL_QUANTITY_REGEX.test(value)) return;
        setQuantityText(value);
    };

    const commitQuantityText = () => {
        const value = quantityText.trim();

        if (value === "" || value === ".") {
            extraRowProps.onChangeMaterialRequestQuantity(item.id, 0);
            return;
        }

        const secondaryQuantity = Number.parseFloat(value);
        if (!Number.isFinite(secondaryQuantity)) return;

        const primaryQuantity = roundQuantity(secondaryQuantity * (item.conversion_rate || 1));
        extraRowProps.onChangeMaterialRequestQuantity(item.id, primaryQuantity);
        setQuantityText(formatEditableQuantity(secondaryQuantity));
    };

    return !item.isSummary ? (
        <TableRow
            sx={{
                backgroundColor,
                "& .MuiTableCell-root": ROW_CELL_SX,
            }}
        >
            <StyledTableCell
                align="left"
                sx={{
                    position: "sticky",
                    left: 0,
                    zIndex: 1,
                    backgroundColor: backgroundColor || "#fff",
                    minWidth: STICKY_COL_1_WIDTH,
                    width: STICKY_COL_1_WIDTH,
                    maxWidth: STICKY_COL_1_WIDTH,
                    px: "8px",
                }}
            >
                <span title={item.material_code ? item.material_code : item.id} style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    <StyledNavLink to={`/materials/${item.id}`}>
                        {item.material_code ? item.material_code : item.id}
                    </StyledNavLink>
                </span>
            </StyledTableCell>
            <StyledTableCell
                align="left"
                sx={{
                    position: "sticky",
                    left: STICKY_COL_2_LEFT,
                    zIndex: 1,
                    backgroundColor: backgroundColor || "#fff",
                    minWidth: MATERIAL_NAME_WIDTH,
                    width: MATERIAL_NAME_WIDTH,
                    maxWidth: MATERIAL_NAME_WIDTH,
                    px: "8px",
                }}
            >
                <span title={item.name} style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {item.name}
                </span>
            </StyledTableCell>
            <StyledTableCell align="center"
                             sx={{ minWidth: UNIT_COL_WIDTH, width: UNIT_COL_WIDTH, maxWidth: UNIT_COL_WIDTH, px: "6px" }}>{item.measurement_unit}</StyledTableCell>
            {/* { canSeePrice && (
                <StyledTableCell align="left">{provider_price}</StyledTableCell>
            )} */}
            <StyledTableCell align="right" sx={{ minWidth: QUANTITY_COL_WIDTH, width: QUANTITY_COL_WIDTH, maxWidth: QUANTITY_COL_WIDTH, px: "8px" }}>{formatNumber(quantity ?? "")}</StyledTableCell>
            <StyledTableCell align="center" sx={{
                minWidth: UNIT_COL_WIDTH,
                width: UNIT_COL_WIDTH,
                maxWidth: UNIT_COL_WIDTH,
                px: "6px",
            }}>{item.secondary_measurement_unit}</StyledTableCell>
            <StyledTableCell align="right" sx={{ minWidth: QUANTITY_COL_WIDTH, width: QUANTITY_COL_WIDTH, maxWidth: QUANTITY_COL_WIDTH, px: "8px" }}>
                <TextBox
                    style={{
                        borderColor: selectedQuantityBorderColor,
                        backgroundColor,
                        width: "100%",
                        minWidth: 0,
                        boxSizing: "border-box",
                        height: "34px",
                        paddingLeft: "8px",
                        paddingRight: "8px",
                        textAlign: "right",
                    }}
                    text={quantityText}
                    onFocus={() => setIsEditingQuantity(true)}
                    onBlur={() => {
                        setIsEditingQuantity(false);
                        commitQuantityText();
                    }}
                    onChangeText={onChangeQuantityText}
                    disabled={extraRowProps.isView}
                />
            </StyledTableCell>
            <StyledTableCell align="right" sx={{ minWidth: QUANTITY_COL_WIDTH, width: QUANTITY_COL_WIDTH, maxWidth: QUANTITY_COL_WIDTH, px: "8px" }}>
                {item.exported_quantity ? formatNumber(item.exported_quantity) : 0}
            </StyledTableCell>
            <StyledTableCell align="center" sx={{ minWidth: ACTION_COL_WIDTH, width: ACTION_COL_WIDTH, maxWidth: ACTION_COL_WIDTH, px: "6px" }}>
                {!extraRowProps.isView && (
                    <IconButton onClick={() => extraRowProps.removeItem(item)}>
                        <DeleteIcon />
                    </IconButton>
                )}
            </StyledTableCell>
        </TableRow>
    ) : (
        <SummaryTableRow>
            <SummaryTableCell
                align="left"
                sx={{ position: "sticky", left: 0, zIndex: 11, minWidth: STICKY_COL_1_WIDTH, width: STICKY_COL_1_WIDTH }}
            >
                {item.id}
            </SummaryTableCell>
            <SummaryTableCell
                align="left"
                sx={{ position: "sticky", left: STICKY_COL_2_LEFT, zIndex: 11, minWidth: MATERIAL_NAME_WIDTH, width: MATERIAL_NAME_WIDTH }}
            >
                {item.name}
            </SummaryTableCell>
            <SummaryTableCell align="left" sx={{ minWidth: UNIT_COL_WIDTH, width: UNIT_COL_WIDTH, maxWidth: UNIT_COL_WIDTH }}></SummaryTableCell>
            <SummaryTableCell align="left" sx={{ minWidth: QUANTITY_COL_WIDTH, width: QUANTITY_COL_WIDTH, maxWidth: QUANTITY_COL_WIDTH }}></SummaryTableCell>
            <SummaryTableCell align="left" sx={{ minWidth: UNIT_COL_WIDTH, width: UNIT_COL_WIDTH, maxWidth: UNIT_COL_WIDTH }}></SummaryTableCell>
            <SummaryTableCell align="left" sx={{ minWidth: QUANTITY_COL_WIDTH, width: QUANTITY_COL_WIDTH, maxWidth: QUANTITY_COL_WIDTH }}></SummaryTableCell>
            <SummaryTableCell align="left" sx={{ minWidth: QUANTITY_COL_WIDTH, width: QUANTITY_COL_WIDTH, maxWidth: QUANTITY_COL_WIDTH }}></SummaryTableCell>
            <SummaryTableCell align="left" sx={{ minWidth: ACTION_COL_WIDTH, width: ACTION_COL_WIDTH, maxWidth: ACTION_COL_WIDTH }}></SummaryTableCell>
        </SummaryTableRow>
    );
};

interface MaterialListProps {
    materials: Material[];
    mergeMaterialRequest: Function;
    isView?: boolean;
    useOtherMaterials?: boolean;
}

export const MaterialList = ({ materials, mergeMaterialRequest, isView, useOtherMaterials }: MaterialListProps) => {
    const {
        user: { role },
    } = useContext(AuthenticationContext);
    // NPQT: confirm if storage keeper can see price
    // const canSeePrice = ![ROLE.FIRST_STORAGE_KEEPER, ROLE.SECOND_STORAGE_KEEPER].includes(role);

    const [isMaterialModalOpen, setIsMaterialModalOpen] = useState(false);
    const [originalQuantities, setOriginalQuantities] = useState<{
        [key: string]: number;
    }>({});


    let hasInvalidQuantity = false;

    const summaryMaterials = {
        id: `Tổng ${materials.length} vật tư`,
        name: "Subtotal",
        price: sum(materials.map((item: any) => (item.provider_price || 0) * (item.missing_quantity || 0))),
        isSummary: true,
    };

    const tableItems = materials.length === 0 ? [] : [...materials, summaryMaterials];

    const onRemoveMaterial = (material: Material) => {
        mergeMaterialRequest({
            materials: materials.filter((item) => item.id !== material.id),
        });
    };

    const onChangeMaterialRequestQuantity = (id: string, quantity: number) => {
        mergeMaterialRequest({
            materials: materials.map((material) =>
                material.id === id ? { ...material, missing_quantity: quantity } : material,
            ),
        });
    };

    const onChangeProvider = (id: string, provider: any) => {
        mergeMaterialRequest({
            materials: materials.map((material: any) => {
                if (material.id === id) {
                    const selectedProvider = material.providers.find((p: any) => p.provider.name === provider.provider.name);
                    return {
                        ...material,
                        provider_name: provider.provider.name,
                        provider_price: selectedProvider ? selectedProvider.price : 0,
                    };
                }
                return material;
            }),
        });
    };

    const getColumns = () => {
        const baseColumns = [
            ...MaterialTableColumns.slice(0, 4),
            ...MaterialTableColumns.slice(4),
        ];

        // if (!canSeePrice) {
        //     return baseColumns.filter((column) => column.text !== "Giá cung cấp");
        // }

        return baseColumns;
    };

    return (
        <>
            {isMaterialModalOpen && !isView && (
                <AddMaterialModal
                    selectedMaterials={materials}
                    open={isMaterialModalOpen}
                    handleClose={() => setIsMaterialModalOpen(false)}
                    mergeMaterialRequest={mergeMaterialRequest}
                    useOtherMaterials={useOtherMaterials}
                />
            )}

            <ContentWrapper>
                <ProductTableText>Danh sách vật tư</ProductTableText>

                {hasInvalidQuantity && (
                    <TableWarningMessage>
                        <WarningTableIcon />
                        <span
                            style={{
                                display: "flex",
                                alignItems: "center",
                                whiteSpace: "pre-wrap",
                            }}
                        >
                            <span style={{ fontWeight: "700" }}>Lưu ý quan trọng: </span>
                            Số lượng yêu cầu không được nhỏ hơn số lượng thiếu.
                        </span>
                    </TableWarningMessage>
                )}

                <CustomTable
                    itemList={tableItems}
                    RowElement={MaterialRow}
                    TableColumns={getColumns()}
                    extraRowProps={{
                        removeItem: onRemoveMaterial,
                        onChangeMaterialRequestQuantity,
                        onChangeProvider,
                        originalQuantities,
                        isView,
                    }}
                />

                {materials.length === 0 && "Không có vật tư được chọn"}
                {!isView && (
                    <ProductButtonRow>
                        <StyledButton variant="contained" onClick={() => setIsMaterialModalOpen(true)}>
                            Thêm vật tư
                        </StyledButton>
                    </ProductButtonRow>
                )}
            </ContentWrapper>
        </>
    );
};
